<template>
	<div class="relative w-full group" @pointerdown="state.isDrag = true" @pointerup="state.isDrag = false">
		<OlMap ref="mapComp" class="w-full h-full" style="">
			<OlView
				:rotation="rotation"
				:projection="projection"
				:center="initCenter"
				@center-changed="onCenterChange"
				:zoom="initZoom"
				@zoom-changed="onZoomChange"
				:enable-rotation="false"
				:multi-world="false"
				:extent="extent"
				:max-zoom="maxZoom"
				:min-zoom="minZoom"
			/>

			<OlTileLayer>
				<OlSourceXYZ
					crossOrigin="anonymous"
					:url="
						'https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}@2x.png?key=' +
						runtimeConfig.public.mapTilerApiKey
					"
					:tile-size="512"
					:tile-pixel-ratio="1"
					:attributions="attributions"
					:attributions-collapsible="false"
				/>
			</OlTileLayer>

			<OlVectorLayer v-if="!editable">
				<OlSourceVector>
					<OlFeature>
						<OlGeomPoint :coordinates="transform(props.center, 'EPSG:4326', projection)"> </OlGeomPoint>
						<OlStyle>
							<OlStyleIcon :src="markerIcon" :scale="0.75" />
						</OlStyle>
					</OlFeature>
				</OlSourceVector>
			</OlVectorLayer>

			<OlZoomControl />
		</OlMap>
		<span
			v-if="editable"
			:class="[
				'absolute left-0 top-0 right-0 bottom-[50%] z-[1] flex items-end justify-center pointer-events-none'
			]"
		>
			<div :class="['flex flex-col items-center']">
				<span :class="['duration-300 z-[1]', state.isDrag ? 'mb-4' : 'mb-0']">
					<Icon
						name="ion:location"
						:class="['w-12 h-12 -mb-1 text-primary']"
						:style="{
							filter: 'drop-shadow(0px 2px 4px rgb(0 0 0 / 0.35))'
						}"
					/>
				</span>
				<span
					:class="[
						'w-1 h-1 rounded-full -mt-0.5 duration-300 outline outline-white absolute bottom-0 -mb-0.5 z-0 shadow-md',
						state.isDrag ? 'opacity-100 bg-black' : 'opacity-0 bg-primary'
					]"
				/>
				<span
					:class="[
						'w-8 h-4 rounded-[100%] bg-black bg-opacity-20 duration-300 absolute bottom-0 -mb-2 z-0',
						state.isDrag ? 'opacity-0' : 'opacity-100'
					]"
				/>
			</div>
		</span>
	</div>
</template>

<script setup lang="ts">
import { Map } from "ol"
import { transform } from "ol/proj"
import {
	Map as MapComps,
	Layers as LayersComps,
	Sources as SourcesComps,
	MapControls as MapControlsComps,
	Geometries as GeometriesComps,
	Styles as StylesComps
} from "vue3-openlayers"
import markerIcon from "@/assets/img/marker-icon-2x-gold.png"

const { OlMap, OlView, OlFeature } = MapComps
const { OlTileLayer, OlVectorLayer } = LayersComps
const { OlSourceXYZ, OlSourceVector } = SourcesComps
const { OlGeomPoint } = GeometriesComps
const { OlStyle, OlStyleIcon } = StylesComps
const { OlZoomControl } = MapControlsComps

const runtimeConfig = useRuntimeConfig()

const emit = defineEmits<{
	"update:center": [number[]]
	"update:zoom": [number]
}>()

const props = withDefaults(
	defineProps<{
		center?: number[]
		zoom?: number
		editable?: boolean
	}>(),
	{
		center: () => [34.0522, -118.2437],
		zoom: () => 6,
		editable: true
	}
)

const state = reactive({
	isDrag: false
})

const mapComp = ref()
const map = ref<Map>()
const projection = ref("EPSG:3857")
const initCenter = ref(props.center ? transform(props.center, "EPSG:4326", projection.value) : undefined)
const initZoom = ref(props.zoom)
const center = ref(props.center)
const zoom = ref(props.zoom)
const rotation = ref(0)
const maxZoom = ref(22)
const minZoom = ref(2)
const extent = ref([-20037508.342789244, -19971868.880408585, 20037508.342789244, 19971868.880408585])
const attributions = ref(
	'<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>'
)

watch(
	() => props.center,
	() => {
		if (center.value?.[0] == props.center?.[0] && center.value?.[1] == props.center?.[1]) {
			return
		}
		if (!center.value) return
		initCenter.value = transform(props.center, "EPSG:4326", projection.value)
		center.value = props.center
	},
	{
		immediate: true
	}
)

watch(
	() => props.zoom,
	() => {
		if (zoom.value == props.zoom) {
			return
		}
		if (!props.zoom) return
		initZoom.value = props.zoom
		zoom.value = props.zoom
	},
	{
		immediate: true
	}
)

watch(
	mapComp,
	() => {
		const m = mapComp.value?.map || null
		if (!m || m == map.value) return
		map.value = m
	},
	{
		immediate: true
	}
)

watch(map, () => {})

const onCenterChange = (v: number[]) => {
	const c = transform(v, projection.value, "EPSG:4326")
	center.value = c
	emit("update:center", c)
}

const onZoomChange = (v: number) => {
	zoom.value = v
	emit("update:zoom", v)
}
</script>

<style scoped>
.overlay-content {
	background: #c84031;
	color: white;
	box-shadow: 0 5px 10px rgb(2 2 2 / 20%);
	padding: 10px 20px;
	font-size: 16px;
}
</style>
